/**
 * `window.__game` (docs/spec/06 §6): enough to drive and inspect the game from a console or a
 * Playwright test without a hand on the keyboard.
 */

import type { Controls } from '../input/controls';
import type { Dir } from '../sim/types';
import type { MenuAction, Route } from '../ui/scene';
import type { App } from './host';
import { GameScene } from './game/gameScene';

export interface DebugApi {
  route(): Route | null;
  go(route: Route): void;
  action(a: MenuAction): void;
  /** Hold a direction (0 none, 1 up, 2 right, 3 down, 4 left) and fire until changed. */
  hold(dir: Dir, fire?: boolean): void;
  /** Run `ms` of frames at once (a background tab gets no animation frames). */
  advance(ms: number): void;
  /** The running cave, when a game is on screen. */
  cave(): unknown;
  /** Stop (or restart) real time: frozen, only `advance` moves the game. */
  freeze(on: boolean): void;
}

declare global {
  interface Window {
    __game?: DebugApi;
  }
}

export function installDebugApi(app: App, controls: Controls): void {
  window.__game = {
    route: () => app.route,
    go: (r) => app.go(r),
    action: (a) => app.top?.action?.(a),
    hold(dir, fire = false) {
      controls.latch.release('debug');
      controls.latch.fireUp('debug');
      if (dir !== 0) controls.latch.press('debug', dir);
      if (fire) controls.latch.fireDown('debug');
    },
    advance(ms) {
      for (let t = 0; t < ms; t += 16) app.frame(16);
    },
    freeze(on) {
      app.frozen = on;
    },
    cave() {
      const top = app.top;
      return top instanceof GameScene ? top.debugState() : null;
    },
  };
}
